import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Dialog,
  CircularProgress,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { validateJson } from 'utils/validateOnDemandConfig';
import { useMessages } from 'modules/intl';
import { ConnectorInfo } from 'types/types';
import { postRequest } from 'utils/helper';
import { ToastSnackBar } from '../Setting/ToastSnackBar';
import Img from './images/Deltek_Replicon.logo.svg';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2.5)
  },
  stack: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: theme.spacing(2)
  },
  box: {
    alignItems: 'center',
    justifyContent: 'center'
  },
  image: {
    height: theme.spacing(12.5),
    width: theme.spacing(28.75)
  },
  arrowIconBack: {
    width: theme.spacing(2.25),
    height: theme.spacing(2.25),
    marginLeft: theme.spacing(3)
  },
  arrowIconForward: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    marginLeft: theme.spacing(3)
  },
  containerCol: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  textField: {
    marginBottom: theme.spacing(3.75)
  },
  connectIndicator: {
    verticalAlign: 'middle',
    marginRight: theme.spacing(6.5)
  }
}));

type RepliconConnectorInstallPageProps = {
  info: ConnectorInfo;
  open: boolean;
  action: string;
  handleInstallDialogClose: () => void;
};

const messages = ['repliconConnectorInstallDialog.inputError'];

export const RepliconConnectorInstallPage: React.FC<RepliconConnectorInstallPageProps> = ({
  info,
  open,
  action,
  handleInstallDialogClose
}: RepliconConnectorInstallPageProps) => {
  const [inputJson, setInputJson] = useState('');
  const [inputError, setInputError] = useState('');
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const errorMessage = useMessages(messages);

  const requiredInputError =
    errorMessage['repliconConnectorInstallDialog.inputError'];
  const handleInputChange = useCallback(
    (event: any) => {
      const value = event.target.value;
      setInputJson(value);
      if (value.trim() === '') setInputError(requiredInputError);
      else {
        setInputError('');
      }
    },
    [requiredInputError]
  );

  const onSnackbarClose = useCallback(() => {
    setIsSnackBarOpen(false);
  }, []);
  const onSnackbarOpen = useCallback(() => {
    setIsSnackBarOpen(true);
  }, []);

  const handleApiCall = useCallback(
    async (action: string) => {
      try {
        const body = {
          connectorName: info.shortName,
          companyKey: info.companyKey,
          environment: 'production',
          inputJson: JSON.parse(inputJson),
          action: action
        };
        const res = await postRequest(
          '/integration-settings-api/install-repliconbased-connector',
          body,
          false
        );
        if (res.data) {
          setConnected(true);
        }
      } catch (error: any) {
        onSnackbarOpen();
        setLoading(false);
      }
    },
    [info.companyKey, info.shortName, inputJson, onSnackbarOpen]
  );

  const handleConnect = (event: any, action: string) => {
    setLoading(true);
    event.preventDefault();
    try {
      const isInvalid = validateJson(JSON.parse(inputJson));
      if (isInvalid) {
        setInputError(requiredInputError);
        setLoading(false);
      }
      if (!isInvalid && inputJson.trim() !== '') {
        handleApiCall(action);
      }
    } catch (error: any) {
      setInputError(requiredInputError);
      setLoading(false);
    }
  };

  const alertMessage = useMemo(() => {
    return formatMessage({
      id: 'repliconConnectorInstallDialog.snackBarErrorMessage'
    });
  }, [formatMessage]);

  setTimeout(() => {
    if (connected) {
      window.location.reload();
    }
  }, 2000);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleInstallDialogClose}
        data-testid="check-all-contents"
        maxWidth="xl"
      >
        <DialogTitle
          className={classes.header}
          data-testid="check-dialog-title"
        >
          <FormattedMessage id={'repliconConnectorInstallDialog.header'} />
        </DialogTitle>
        <DialogContent>
          <Stack className={classes.stack}>
            <img
              src={Img}
              alt="Deltek Replicon Logo"
              className={classes.image}
              data-testid="render-deltek-replicon-logo"
            />
          </Stack>
          <Box className={classes.box} data-testid="render-box">
            <div>
              <br />
              <TextField
                id="inputJson"
                variant="outlined"
                required
                multiline
                fullWidth
                value={inputJson}
                onChange={handleInputChange}
                error={Boolean(inputError)}
                label={
                  <FormattedMessage
                    id={'repliconConnectorInstallDialog.reportConfig.label'}
                  />
                }
                type={'text'}
                helperText={inputError ? inputError : ''}
                className={classes.textField}
                inputProps={{ 'data-testid': 'inputJson' }}
              />
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleInstallDialogClose}
            color="grey"
            autoFocus
            variant="contained"
            data-testid="cancel-button"
          >
            <FormattedMessage id={'connectorSetting.cancel'} />
          </Button>
          {loading ? (
            <>
              <CircularProgress
                size={22}
                data-testid="connect-progress"
                className={classes.connectIndicator}
              />
            </>
          ) : (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              onClick={(event) => handleConnect(event, action)}
              data-testid="connect-button"
            >
              <FormattedMessage
                id={'repliconConnectorInstallDialog.connectButton'}
              />
            </Button>
          )}
        </DialogActions>
        <ToastSnackBar
          show={isSnackBarOpen}
          category={'error'}
          message={alertMessage}
          onClose={onSnackbarClose}
        />
      </Dialog>
    </>
  );
};
